// ✅ Updated EditContentForm.js
// frontend/src/features/content/EditContentForm.js
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useUpdateContentMutation } from './contentApiSlice';
import { useGetAllCategoriesAdminQuery, selectAllCategoriesForAdminByArgs } from '../categories/categoriesApiSlice';
import { useSelector } from 'react-redux';
import useTitle from '../../hooks/useTitle';

const EditContentForm = ({ contentItem }) => {
    useTitle(`Nirakar Satguru: Edit ${contentItem?.title || 'Content'}`);
    const navigate = useNavigate();

    const [updateContent, { isLoading, isSuccess, isError, error }] = useUpdateContentMutation();

    const [title, setTitle] = useState(contentItem.title);
    
    const [textContent, setTextContent] = useState(contentItem.text_content || '');
    const [selectedCategory, setSelectedCategory] = useState(contentItem.category?._id || '');
    const [imageUrl, setImageUrl] = useState(contentItem.imageUrl || '');
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(contentItem.imageUrl || '');
    const [isActive, setIsActive] = useState(contentItem.isActive);
    const [contentType, setContentType] = useState(contentItem.contentType || 'text');
    const [Clanguage, setLanguage] = useState(contentItem.Clanguage || 'hi');

    const { data: categoriesResult } = useGetAllCategoriesAdminQuery({ page: 1, search: '', isActive: 'true' });
    const categories = useSelector(state => selectAllCategoriesForAdminByArgs(state, { page: 1, search: '', isActive: 'true' }));

    useEffect(() => {
        if (imageFile) {
            const reader = new FileReader();
            reader.onloadend = () => setImagePreview(reader.result);
            reader.readAsDataURL(imageFile);
        } else {
            setImagePreview(contentItem.imageUrl || '');
        }
    }, [imageFile, contentItem.imageUrl]);

    useEffect(() => {
        if (isSuccess) {
            navigate('/dashboard/contents');
        }
    }, [isSuccess, navigate]);

    const canSave = [title, textContent, selectedCategory, contentType, Clanguage].every(Boolean) && !isLoading;

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setImageFile(e.target.files[0]);
        } else {
            setImageFile(null);
            setImagePreview(contentItem.imageUrl || '');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSave) return;

        const dataToSend = new FormData();
        dataToSend.append('title', title);
       
        dataToSend.append('text_content', textContent);
        dataToSend.append('category', selectedCategory);
        dataToSend.append('isActive', isActive);
        dataToSend.append('contentType', contentType);
        dataToSend.append('Clanguage', Clanguage);
        if (imageFile) dataToSend.append('file', imageFile);

        try {
            await updateContent({ id: contentItem.id, formData: dataToSend }).unwrap();
        } catch (err) {
            console.error('Failed to update content:', err);
        }
    };

    if (!contentItem) {
        return <Alert variant="danger">Content data not provided to form.</Alert>;
    }

    return (
        <div className="container mt-4">
            <h2>Edit Content: {contentItem.title}</h2>
            {isError && <Alert variant="danger">{error?.data?.message || 'Failed to update content.'}</Alert>}
            {isSuccess && <Alert variant="success">Content updated successfully!</Alert>}

            <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="title">
                    <Form.Label>Title:</Form.Label>
                    <Form.Control type="text" value={title} onChange={(e) => setTitle(e.target.value)} required />
                </Form.Group>

                

                <Form.Group className="mb-3" controlId="textContent">
                    <Form.Label>Main Text Content:</Form.Label>
                    <Form.Control as="textarea" rows={5} value={textContent} onChange={(e) => setTextContent(e.target.value)} required />
                </Form.Group>

                <Form.Group className="mb-3" controlId="category">
                    <Form.Label>Category:</Form.Label>
                    <Form.Select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} required>
                        <option value="">Select a category</option>
                        {categories?.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                    </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3" controlId="contentType">
                    <Form.Label>Content Type:</Form.Label>
                    <Form.Select value={contentType} onChange={(e) => setContentType(e.target.value)} required>
                        <option value="">Select content type</option>
                        <option value="text">Text</option>
                        <option value="audio">Audio</option>
                        <option value="video">Video</option>
                        <option value="pdf">PDF</option>
                        <option value="image">Image</option>
                    </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3" controlId="Clanguage">
                    <Form.Label>Language:</Form.Label>
                    <Form.Select value={Clanguage} onChange={(e) => setLanguage(e.target.value)} required>
                        <option value="hi">Hindi</option>
                        <option value="en">English</option>
                    </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3" controlId="imageFile">
                    <Form.Label>Content Image (Optional):</Form.Label>
                    <Form.Control type="file" onChange={handleFileChange} accept="image/*" />
                    {imagePreview && <img src={imagePreview} alt="Preview" style={{ maxWidth: '150px', height: 'auto' }} className="rounded mt-2" />}
                </Form.Group>

                <Form.Group className="mb-3" controlId="isActive">
                    <Form.Check type="checkbox" label="Is Active" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                </Form.Group>

                <Button variant="primary" type="submit" disabled={!canSave}>
                    {isLoading ? <Spinner animation="border" size="sm" /> : 'Update Content'}
                </Button>
                <Button variant="secondary" onClick={() => navigate('/dashboard/contents')} className="ms-2">Cancel</Button>
            </Form>
        </div>
    );
};

export default EditContentForm;